import { userClient } from '../config/api';
import {
  ApiResponse,
  InspectorUser,
  CreateInspectorUserRequest,
  UpdateInspectorUserRequest,
  GetAllInspectorUsersParams,
  SearchInspectorByCpfParams,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class InspectorUserService {
  private readonly basePath = '/api/users/inspectors';

  createInspectorUser = async (data: CreateInspectorUserRequest): Promise<InspectorUser> => {
    try {
      const response = await userClient.post<ApiResponse<InspectorUser>>(this.basePath, data);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error creating inspector user');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao criar inspetor');
    }
  };

  getAllInspectorUsers = async (params?: GetAllInspectorUsersParams): Promise<InspectorUser[]> => {
    try {
      const response = await userClient.get<ApiResponse<any>>(this.basePath, {
        params: {
          page: 1,
          pageSize: 1000,
          ...params
        },
      });

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching inspector users');
      }

      const data = response.data.data;
      if (data && typeof data === 'object' && 'data' in data) {
        return data.data || [];
      }

      return data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching inspector users');
    }
  };

  getInspectorUserById = async (id: string): Promise<InspectorUser> => {
    try {
      const response = await userClient.get<ApiResponse<InspectorUser>>(`${this.basePath}/${id}`);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching inspector user');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching inspector user');
    }
  };

  updateInspectorUser = async (
    id: string,
    data: UpdateInspectorUserRequest
  ): Promise<InspectorUser> => {
    try {
      const response = await userClient.put<ApiResponse<InspectorUser>>(
        `${this.basePath}/${id}`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error updating inspector user');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao atualizar inspetor');
    }
  };

  deleteInspectorUser = async (id: string): Promise<void> => {
    try {
      const response = await userClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting inspector user');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting inspector user');
    }
  };

  searchInspectorByCpf = async (params: SearchInspectorByCpfParams): Promise<InspectorUser> => {
    try {
      const response = await userClient.get<ApiResponse<InspectorUser>>(`${this.basePath}/search`, {
        params,
      });

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error searching inspector by CPF');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error searching inspector by CPF');
    }
  };
}

export const inspectorUserService = new InspectorUserService();
